/**
 * All bakery-facing dates are reckoned in the shop's timezone, not the device's.
 * A rider opening the app on a phone still set to UTC must see the same
 * "today" as the register on the counter.
 */
export const SHOP_TIMEZONE = 'Asia/Manila';
const isoDateFormatter = new Map();
function dateFormatter(timeZone) {
    let formatter = isoDateFormatter.get(timeZone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        isoDateFormatter.set(timeZone, formatter);
    }
    return formatter;
}
/** The calendar date in the shop's timezone, as YYYY-MM-DD. */
export function shopDate(instant = new Date(), timeZone = SHOP_TIMEZONE) {
    const date = typeof instant === 'string' ? new Date(instant) : instant;
    // en-CA formats as YYYY-MM-DD, which is exactly the shape we want.
    return dateFormatter(timeZone).format(date);
}
export function addDays(isoDate, days) {
    const [year, month, day] = isoDate.split('-').map(Number);
    const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}
export function daysBetween(fromIso, toIso) {
    const from = Date.parse(`${fromIso}T00:00:00Z`);
    const to = Date.parse(`${toIso}T00:00:00Z`);
    return Math.round((to - from) / 86_400_000);
}
/** Weekday index in the shop's timezone. 0 = Sunday. */
export function shopWeekday(instant = new Date(), timeZone = SHOP_TIMEZONE) {
    const iso = shopDate(instant, timeZone);
    return new Date(`${iso}T00:00:00Z`).getUTCDay();
}
/**
 * Which tab an order belongs on. `dueAt` is a full timestamp; the bucket is
 * decided by its calendar date in the shop's timezone, except that a dated
 * order whose moment has already passed is overdue regardless of the day.
 */
export function dueBucketFor(dueAt, now = new Date(), timeZone = SHOP_TIMEZONE) {
    if (!dueAt)
        return 'undated';
    const today = shopDate(now, timeZone);
    const due = shopDate(dueAt, timeZone);
    if (due < today)
        return 'overdue';
    if (due === today) {
        return Date.parse(dueAt) < now.getTime() ? 'overdue' : 'today';
    }
    if (due === addDays(today, 1))
        return 'tomorrow';
    return 'advance';
}
export const DUE_BUCKET_LABELS = {
    overdue: 'Overdue',
    today: 'Today',
    tomorrow: 'Tomorrow',
    advance: 'Advance',
    undated: 'No date',
};
/** "in 2 hours", "3 days ago". Used on the orders board and in the inbox. */
export function relativeTime(instant, now = new Date()) {
    const target = typeof instant === 'string' ? Date.parse(instant) : instant.getTime();
    const diffSeconds = Math.round((target - now.getTime()) / 1000);
    const absolute = Math.abs(diffSeconds);
    const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    if (absolute < 45)
        return 'just now';
    if (absolute < 3_600)
        return formatter.format(Math.round(diffSeconds / 60), 'minute');
    if (absolute < 86_400)
        return formatter.format(Math.round(diffSeconds / 3_600), 'hour');
    if (absolute < 604_800)
        return formatter.format(Math.round(diffSeconds / 86_400), 'day');
    if (absolute < 2_629_800)
        return formatter.format(Math.round(diffSeconds / 604_800), 'week');
    if (absolute < 31_557_600)
        return formatter.format(Math.round(diffSeconds / 2_629_800), 'month');
    return formatter.format(Math.round(diffSeconds / 31_557_600), 'year');
}
/** Short shop-local time, e.g. "3:45 PM". */
export function formatShopTime(instant, timeZone = SHOP_TIMEZONE) {
    const date = typeof instant === 'string' ? new Date(instant) : instant;
    return new Intl.DateTimeFormat('en-PH', {
        timeZone,
        hour: 'numeric',
        minute: '2-digit',
    }).format(date);
}
/** "Sat, 30 Aug" — dense enough for list rows. */
export function formatShopDate(instant, timeZone = SHOP_TIMEZONE, options = { weekday: 'short', day: 'numeric', month: 'short' }) {
    const date = typeof instant === 'string' ? new Date(instant) : instant;
    return new Intl.DateTimeFormat('en-PH', { timeZone, ...options }).format(date);
}
/** Start-of-day and end-of-day instants for a shop calendar date. */
export function dayRange(isoDate, timeZone = SHOP_TIMEZONE) {
    // Manila has had no DST since 1978, so a fixed +08:00 offset is exact here and
    // avoids pulling in a full timezone library for the common case.
    const offset = timeZone === SHOP_TIMEZONE ? '+08:00' : 'Z';
    return {
        from: new Date(`${isoDate}T00:00:00${offset}`).toISOString(),
        to: new Date(`${addDays(isoDate, 1)}T00:00:00${offset}`).toISOString(),
    };
}
//# sourceMappingURL=dates.js.map